// KPI metrik / formül / birim değişimi şemaları — schemas/kpi.ts 600 satır bölmesi (TODO-KPI-114/-144, salt taşıma); `kpi.ts` barrel'i yeniden dışa açar.
import { z } from "zod";
import {
  KPI_AGGREGATIONS,
  KPI_METRIC_STATUSES,
  KPI_NODE_KINDS,
  KPI_VALUE_TYPES,
  KpiBreakdownSchema,
} from "./kpi-constants.js";

// ---- Formül varyantları (Eng 9a: en fazla bir aktif) ----
export const KpiFormulaSchema = z.object({
  id: z.string().uuid(),
  label: z.string().min(1),
  expression: z.string().min(1).max(2000),
  isActive: z.boolean(),
  createdAt: z.string().optional(),
  createdBy: z.string().uuid().nullable().optional(),
});
export type KpiFormula = z.infer<typeof KpiFormulaSchema>;
export const KpiFormulaListSchema = z
  .array(KpiFormulaSchema)
  .refine((list) => list.filter((f) => f.isActive).length <= 1, {
    message: "En fazla bir formül aktif olabilir",
  });
export type KpiFormulaList = z.infer<typeof KpiFormulaListSchema>;

// ---- Değer kaynağı ----
export const KpiValueSourceSchema = z.object({
  kind: z.enum(["manual", "import", "client", "api"]),
  jobId: z.string().uuid().optional(),
});
export type KpiValueSource = z.infer<typeof KpiValueSourceSchema>;

// ---- Metrik (kpi_metrics — K18 canonical kütüphane, client_id NULL = global) ----
export const KpiMetricRequirementSchema = z.object({
  standard: z.string().min(1),
  code: z.string().min(1),
  mappingType: z.string().optional(),
  note: z.string().optional(),
});
export type KpiMetricRequirement = z.infer<typeof KpiMetricRequirementSchema>;

export const KpiMetricSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  clientId: z.string().uuid().nullable(),
  parentId: z.string().uuid().nullable(),
  label: z.string(),
  description: z.string().nullable(),
  defaultUnit: z.string().max(64).nullable(),
  /** Birim varyantı: katalogda önerilen alternatif birimler (varsayılan dışı). */
  unitVariants: z.array(z.string().max(32)).default([]),
  valueType: z.enum(KPI_VALUE_TYPES),
  isDerived: z.boolean(),
  defaultFormulas: KpiFormulaListSchema,
  aggregation: z.enum(KPI_AGGREGATIONS),
  /** TODO-KPI-32: `weighted_avg` ağırlık göstergesi (katalog metriği); diğer kurallarda null. */
  aggregationWeightMetricId: z.string().uuid().nullable(),
  showChangeDefault: z.boolean(),
  increaseIsGoodDefault: z.boolean().nullable(),
  defaultQuestionCode: z.string().nullable(),
  status: z.enum(KPI_METRIC_STATUSES),
  version: z.number().int(),
  orderIndex: z.number().int(),
  /** K2: katalog bandı yalnız set oluşturulduktan SONRA eklenen göstergeleri önerir. */
  createdAt: z.string().optional(),
  /** Kırılım hiyerarşisi: alan yokluğu = kırılımsız (eski DTO byte-eşit). */
  nodeKind: z.enum(KPI_NODE_KINDS).optional(),
  breakdown: KpiBreakdownSchema.nullable().optional(),
  dimensionMember: z.string().nullable().optional(),
  calculationGroupMetricId: z.string().uuid().nullable().optional(),
});
export type KpiMetric = z.infer<typeof KpiMetricSchema>;

/**
 * Finar rev-B 11 — `DELETE /kpi/metrics/:id` 409 `KPI_METRIC_IN_USE` → `details.sets`: göstergeyi
 * içeren veri setleri (müşteri adı + set adı; FE Veri Yönetimi bağlantısı kurar).
 */
export const KpiMetricInUseSetSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  clientId: z.string().uuid(),
  clientName: z.string(),
});
export type KpiMetricInUseSet = z.infer<typeof KpiMetricInUseSetSchema>;

// Girdi: `code` opsiyonel — sunucu `slugifyKpiCode` ile üretir.
/** Gösterge kodu yeniden adlandırma (2026-10-08): dryRun → etkilenen formül sayıları; commit → tek tx. */
export const KpiMetricRenameCodeBodySchema = z.object({
  code: z.string().min(1).max(64),
  dryRun: z.boolean().optional(),
});
export type KpiMetricRenameCodeBody = z.infer<typeof KpiMetricRenameCodeBodySchema>;
export const KpiMetricRenameCodeResultSchema = z.object({
  from: z.string(),
  to: z.string(),
  dryRun: z.boolean(),
  /** varsayılan formülü etkilenen katalog göstergesi sayısı */
  catalogFormulas: z.number().int(),
  /** formül varyantı etkilenen set göstergesi sayısı */
  setFormulas: z.number().int(),
  /** etkilenen set sayısı */
  sets: z.number().int(),
  /** B4/R1 — `{eski}` jetonu geçen `default_unit` şablonu sayısı (yalnız > 0 ya da dryRun'da döner) */
  unitTemplates: z.number().int().optional(),
  /** kırılım: birlikte yeniden adlandırılan `<eski>__<boyut>` grup kodu sayısı ("N grup") */
  groups: z.number().int().optional(),
  /** ilk 10 örnek: "Set adı › gösterge: eski → yeni" */
  samples: z.array(z.string()),
});
export type KpiMetricRenameCodeResult = z.infer<typeof KpiMetricRenameCodeResultSchema>;

export const KpiMetricUpsertSchema = z.object({
  code: z.string().min(1).max(64).optional(),
  label: z.string().min(1).max(200),
  parentId: z.string().uuid().nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  defaultUnit: z.string().max(64).nullable().optional(),
  unitVariants: z.array(z.string().max(32)).max(20).optional(),
  valueType: z.enum(KPI_VALUE_TYPES).optional(),
  isDerived: z.boolean().optional(),
  defaultFormulas: KpiFormulaListSchema.optional(),
  aggregation: z.enum(KPI_AGGREGATIONS).optional(),
  aggregationWeightMetricId: z.string().uuid().nullable().optional(),
  showChangeDefault: z.boolean().optional(),
  increaseIsGoodDefault: z.boolean().nullable().optional(),
  defaultQuestionCode: z.string().max(64).nullable().optional(),
  orderIndex: z.number().int().optional(),
  requirements: z.array(KpiMetricRequirementSchema).optional(),
  /** Kırılım hiyerarşisi (0069): verilmezse `metric` (eski çağıranlar — servis eski kalıbı korur). */
  nodeKind: z.enum(KPI_NODE_KINDS).optional(),
  breakdown: KpiBreakdownSchema.nullable().optional(),
  dimensionMember: z.string().min(1).max(64).nullable().optional(),
});
export type KpiMetricUpsert = z.infer<typeof KpiMetricUpsertSchema>;

// ---- Birim değişimi (müşteri × metrik kalıcı birim) ----
export const KPI_UNIT_CHANGE_MODES = ["convert", "relabel"] as const;
export const KpiUnitChangeBodySchema = z.object({
  clientId: z.string().uuid(),
  metricId: z.string().uuid(),
  unit: z.string().max(32).nullable(),
  mode: z.enum(KPI_UNIT_CHANGE_MODES).optional(),
});
export type KpiUnitChangeBody = z.infer<typeof KpiUnitChangeBodySchema>;

export const KpiUnitChangePreviewSchema = z.object({
  from: z.string().nullable(),
  to: z.string().nullable(),
  factor: z.number().nullable(),
  /** Sayısal değer sayısı — `reportedValues` bunun ALT KÜMESİDİR (raporlanan değerler de burada sayılır). */
  values: z.number(),
  /** `values` içindeki raporlanan (K20) değer sayısı. */
  reportedValues: z.number(),
  targets: z.number(),
  entities: z.number(),
  yearRange: z.object({ min: z.number(), max: z.number() }).nullable(),
  setsAffected: z.number(),
  lossyCount: z.number(),
  lossySample: z
    .object({ raw: z.number(), exact: z.number(), rounded: z.number(), pct: z.number() })
    .nullable(),
  /** DR8 radyo örneği: kapsamdaki gerçek bir hücre (en yeni yıl); çarpan yoksa/1 ise yok. */
  sample: z
    .object({
      value: z.number(),
      converted: z.number(),
      entityName: z.string(),
      year: z.number(),
    })
    .optional(),
  dependentFormulas: z.array(
    z.object({
      setId: z.string().nullable(),
      setName: z.string().nullable(),
      code: z.string(),
      expression: z.string(),
    }),
  ),
  blockers: z.object({
    formulaConflicts: z.array(
      z.object({ setId: z.string().nullable(), code: z.string(), expression: z.string() }),
    ),
    dependents: z.array(
      z.object({
        code: z.string(),
        label: z.string(),
        reported: z.number(),
        targets: z.number(),
      }),
    ),
  }),
  noop: z.boolean(),
});
export type KpiUnitChangePreview = z.infer<typeof KpiUnitChangePreviewSchema>;

export const KpiUnitChangeResultSchema = z.object({
  unit: z.string().nullable(),
  converted: z.number(),
  targetsConverted: z.number(),
  mode: z.enum(KPI_UNIT_CHANGE_MODES),
  setsAffected: z.number(),
});
export type KpiUnitChangeResult = z.infer<typeof KpiUnitChangeResultSchema>;

/** Katalog DR10 — bir metriğin müşteri bazında birim kullanımı. */
export const KpiUnitUsageSchema = z.object({
  clients: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      values: z.number(),
      targets: z.number(),
      unit: z.string().nullable(),
      /** Göstergeyi içeren bu müşterinin veri setleri (bant "Veri Yönetimi'nde aç" için). */
      setIds: z.array(z.string()),
    }),
  ),
});
export type KpiUnitUsage = z.infer<typeof KpiUnitUsageSchema>;
